/**
 * Device Fingerprint Types
 *
 * Signals collected for fraud prevention. All signals are non-PII,
 * publicly available browser APIs, and standard across the fraud
 * prevention industry (Stripe Radar, Sift, Forter, etc.).
 */

/** Browser and environment signals */
export type BrowserSignals = {
  userAgent: string;
  platform: string;
  language: string;
  languages: string[];
  cookieEnabled: boolean;
  doNotTrack: string | null;
  /** True if browser automation (Selenium, Puppeteer) is detected */
  webdriver: boolean;
};

/** Display and hardware signals */
export type HardwareSignals = {
  screenResolution: [number, number];
  screenColorDepth: number;
  devicePixelRatio: number;
  hardwareConcurrency: number;
  /** Device RAM in GB. Chrome/Edge only — null on other browsers */
  deviceMemory: number | null;
  maxTouchPoints: number;
};

/** Timezone and locale signals */
export type TimezoneSignals = {
  timezone: string;
  timezoneOffset: number;
};

/** Canvas rendering fingerprint (hashed) — highest entropy signal */
export type CanvasSignals = {
  canvasHash: string;
};

/** All raw signals collected from the device */
export type DeviceFingerprintSignals = BrowserSignals & HardwareSignals & TimezoneSignals & CanvasSignals;

/** Final fingerprint result with hash and raw data */
export type DeviceFingerprintResult = {
  /** SHA-256 hash of all signals combined — the unique fingerprint ID */
  fingerprintId: string;
  /** Raw signal data for backend querying and analysis */
  signals: DeviceFingerprintSignals;
  /** ISO timestamp of when the fingerprint was collected */
  collectedAt: string;
  /** Version of the fingerprinting module (for backward compatibility) */
  version: string;
};

/** Trigger context for where the fingerprint was collected */
export type FingerprintTriggerContext = {
  trigger: string;
  metadata?: Record<string, string>;
};

/** Payload sent to the backend API */
export type DeviceFingerprintPayload = DeviceFingerprintResult & {
  context: FingerprintTriggerContext;
};

export enum DeviceFingerprintEventType {
  Login = 'login',
  Logout = 'logout',
  Signup = 'signup',
  InvoiceMarkAsPaid = 'invoice_mark_as_paid',
  IssueRefund = 'issue_refund',
  SubmitEvidence = 'submit_evidence'
}
